import { useState, useEffect, useCallback } from "react";

interface Publication {
  id: string;
  offerId: string;
  targetChatId: string;
  status: string;
  attempts: number;
  wahaMessageId: string | null;
  sentAt: string | null;
  errorMessage: string | null;
}

export function PublicationsPage() {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("affiliate_flow_auth_token");
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      const response = await fetch(`/api/v1/publications?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json();
      if (data.success) {
        setPublications(data.data.items || []);
      } else {
        setError(data.error || "Erro ao carregar publicações.");
      }
    } catch {
      setError("Erro de conexão.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const statusLabel: Record<string, string> = {
    pending: "Pendente",
    publishing: "Publicando",
    published: "Publicada",
    failed: "Falha",
    skipped: "Ignorada",
    uncertain: "Incerto",
  };

  const statusColor: Record<string, string> = {
    pending: "bg-yellow-500/10 text-yellow-700",
    publishing: "bg-blue-500/10 text-blue-700",
    published: "bg-green-500/10 text-green-700",
    failed: "bg-red-500/10 text-red-700",
    skipped: "bg-gray-500/10 text-gray-700",
    uncertain: "bg-orange-500/10 text-orange-700",
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="">Todos os status</option>
          <option value="pending">Pendente</option>
          <option value="published">Publicada</option>
          <option value="failed">Falha</option>
          <option value="skipped">Ignorada</option>
          <option value="uncertain">Incerto</option>
        </select>
      </div>

      {error && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : publications.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          Nenhuma publicação registrada.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Destino</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Tentativas</th>
                <th className="px-3 py-2 font-medium">Enviada em</th>
                <th className="px-3 py-2 font-medium">Erro</th>
              </tr>
            </thead>
            <tbody>
              {publications.map((pub) => (
                <tr key={pub.id} className="border-t">
                  <td className="max-w-40 truncate px-3 py-2 font-mono text-xs">
                    {pub.targetChatId}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor[pub.status] || "bg-muted"}`}>
                      {statusLabel[pub.status] || pub.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">{pub.attempts}</td>
                  <td className="px-3 py-2 text-xs">
                    {pub.sentAt ? new Date(pub.sentAt).toLocaleString("pt-BR") : "—"}
                  </td>
                  <td className="max-w-40 truncate px-3 py-2 text-xs text-destructive">
                    {pub.errorMessage || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
